import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X, AlertCircle, Loader2 } from "lucide-react";
import { Toast } from "../components/Toast";
import { ProductSelect } from "../components/ProductSelect";
import { ColorSelect } from "../components/ColorSelect";
import { AgreementProgressSection } from "../components/AgreementProgressSection";
import { Input } from "@/shared/ui/Input";
import { DatePicker } from "@/shared/ui/DatePicker";
import { Textarea } from "@/shared/ui/Textarea";
import {
  useImportedAgreement,
  useUpdateImportedAgreement,
  useProductOptions,
  useColorOptions,
} from "../hooks";
import type { ProductOption } from "../types";

// Opens for a single created agreement from the import-result list. Renders only editable detail
// fields (driven by editable_fields from the API). Money/schedule fields are shown greyed-out for
// context but are NOT inputs. For car/technology types, the model field is a catalogue dropdown.
export function AgreementEditDrawer({
  loanId,
  onClose,
  onSaved,
}: {
  loanId: number;
  onClose: () => void;
  onSaved: (loanId: number, missingCount: number) => void;
}) {
  const { t } = useTranslation();
  const { data: detail, isLoading, isError } = useImportedAgreement(loanId);
  const { mutate: update, isPending: isSaving } = useUpdateImportedAgreement();

  // Determine the loan type for the product-options query.
  const loanType =
    detail?.type === "car" || detail?.type === "technology" ? detail.type : null;
  const { data: productOptions = [], isLoading: isLoadingOptions } = useProductOptions(
    loanType,
    true, // drawer is always open when this component is mounted
  );
  const { data: colorOptions = [], isLoading: colorsLoading } = useColorOptions(true);

  // Local form state: string values keyed by editable_field names.
  const [form, setForm] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed form when detail loads (or reloads).
  useEffect(() => {
    if (!detail) return;
    const seed: Record<string, string> = {};
    detail.editable_fields.forEach((k) => {
      const v = detail.additional_data[k];
      seed[k] = v !== undefined && v !== null ? String(v) : "";
    });
    setForm(seed);
  }, [detail]);

  const handleChange = useCallback(
    (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setSaveError(null);
    },
    [],
  );

  // Handle catalogue option selection: write both name and id into the form payload.
  // CAR:        model + model_type_id
  // TECHNOLOGY: technology_model + technology_model_id
  const handleProductSelect = useCallback(
    (opt: ProductOption) => {
      if (!detail) return;
      setSaveError(null);
      if (detail.type === "car") {
        setForm((prev) => ({
          ...prev,
          model: opt.name,
          model_type_id: String(opt.id),
        }));
      } else if (detail.type === "technology") {
        setForm((prev) => ({
          ...prev,
          technology_model: opt.name,
          technology_model_id: String(opt.id),
        }));
      }
    },
    [detail],
  );

  // Derive currently selected model name from form for the ProductSelect display value.
  function currentModelName(): string {
    if (!detail) return "";
    if (detail.type === "car") return form["model"] ?? "";
    if (detail.type === "technology") return form["technology_model"] ?? "";
    return "";
  }

  const handleSave = useCallback(() => {
    if (!detail) return;
    setSaveError(null);
    update(
      { loanId, additional_data: form },
      {
        onSuccess: (updated) => {
          setToast(t("import.agreements.edit.savedToast"));
          // Notify parent with refreshed missing count so the list badge updates.
          onSaved(loanId, updated.missing_detail_fields.length);
          // Re-seed form from updated data.
          const seed: Record<string, string> = {};
          updated.editable_fields.forEach((k) => {
            const v = updated.additional_data[k];
            seed[k] = v !== undefined && v !== null ? String(v) : "";
          });
          setForm(seed);
          // Auto-close after short delay so user sees the toast.
          setTimeout(onClose, 1200);
        },
        onError: () => setSaveError(t("import.agreements.edit.saveError")),
      },
    );
  }, [detail, loanId, form, update, t, onSaved, onClose]);

  // Determine input type for a field key.
  function inputType(field: string): "date" | "number" | "text" {
    if (field === "production_date") return "date";
    if (field === "mileage") return "number";
    return "text";
  }

  // Fields that are model catalogue fields — replaced with ProductSelect. The editable_fields list
  // from the backend will not include "model" / "technology_model" directly (they come back as
  // additional_data keys), but we insert the dropdown at the logical position when the type requires it.
  const isModelField = (field: string) =>
    (detail?.type === "car" && field === "model") ||
    (detail?.type === "technology" && field === "technology_model");

  // Portal to document.body so that fixed positioning is relative to the viewport, not
  // a transformed/overflow-clipped ancestor container (which would confine the backdrop).
  return createPortal(
    // Overlay
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Drawer panel */}
      <div className="relative z-50 flex max-h-[92dvh] w-full max-w-3xl flex-col rounded-t-card bg-white shadow-float sm:rounded-card lg:max-h-none">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-grey-100 px-4 py-4 sm:px-6">
          <h3 className="text-[16px] font-semibold text-grey-800">
            {t("import.agreements.edit.drawerTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-grey-400 hover:text-grey-700"
            aria-label="close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — two-pane (B): left = read-only context, right = editable fields.
            On mobile the whole modal scrolls; on desktop each pane scrolls independently. */}
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:overflow-visible">
          {isLoading && (
            <div className="space-y-4">
              <div className="h-24 animate-pulse rounded-card bg-grey-100" />
              <div className="h-20 animate-pulse rounded-card bg-grey-100" />
              <div className="grid grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-12 animate-pulse rounded-control bg-grey-100" />
                ))}
              </div>
            </div>
          )}

          {isError && !isLoading && (
            <div className="flex items-center gap-2 rounded-card bg-red-bg px-4 py-3 text-[13px] text-red">
              <AlertCircle size={16} />
              {t("import.agreements.edit.loadError")}
            </div>
          )}

          {detail && !isLoading && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
              {/* LEFT — read-only context: payment progress + locked money summary */}
              <div className="lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
                  {t("import.agreements.detail.contextTitle")}
                </p>

                {/* Payment-progress section (Feature A) — the real schedule, above the locked money.
                    Shown only when the agreement has a payment schedule (imported loans always do). */}
                <AgreementProgressSection
                  payments={detail.payments}
                  paidAmount={detail.paid_amount}
                  remainder={detail.remainder}
                  startDate={detail.first_payment_date ?? detail.payments[0]?.payment_date}
                />

                {/* Locked money/schedule section — context only, no inputs */}
                <div className="rounded-card bg-grey-50 px-4 py-3">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
                    {t("import.agreements.edit.sectionLocked")}
                  </p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                    <div>
                      <span className="text-grey-500">{t("import.agreements.edit.lockedSellingPrice")}: </span>
                      <span className="font-medium text-grey-700">
                        {detail.selling_price.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-grey-500">{t("import.agreements.edit.lockedDownPayment")}: </span>
                      <span className="font-medium text-grey-700">
                        {detail.down_payment.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-grey-500">{t("import.agreements.edit.lockedMonthlyPayment")}: </span>
                      <span className="font-medium text-grey-700">
                        {detail.monthly_payment.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-grey-500">{t("import.agreements.edit.lockedPaymentCount")}: </span>
                      <span className="font-medium text-grey-700">{detail.payment_count}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT — editable fields */}
              <div className="lg:max-h-[70vh] lg:overflow-y-auto lg:pl-1">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
                  {t("import.agreements.detail.editTitle")}
                </p>

                {/* Catalogue model dropdown — only for CAR and TECHNOLOGY types */}
                {(detail.type === "car" || detail.type === "technology") && (
                  <div className="mb-4">
                    <label className="mb-1 block text-[13px] font-medium text-grey-700">
                      {t("import.agreements.edit.model")}
                    </label>
                    <ProductSelect
                      options={productOptions}
                      value={currentModelName()}
                      onChange={handleProductSelect}
                      placeholder={t("import.agreements.edit.modelPlaceholder")}
                      searchPlaceholder={t("import.agreements.edit.modelSearch")}
                      isLoading={isLoadingOptions}
                    />
                  </div>
                )}

                {/* Editable fields — driven by detail.editable_fields, skip model catalogue fields */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {detail.editable_fields
                    .filter((field) => !isModelField(field))
                    .map((field) => {
                      const isMissing = detail.missing_detail_fields.includes(field);
                      const type = inputType(field);
                      // Use a textarea for comment field.
                      const isTextarea = field === "comment";
                      // Look up label — fallback to raw key if not in translations.
                      const labelKey = `import.agreements.edit.fields.${field}` as const;
                      return (
                        <div key={field} className={field === "comment" ? "sm:col-span-2" : ""}>
                          <label
                            htmlFor={`edit-${field}`}
                            className="mb-1 flex items-center gap-1 text-[13px] font-medium text-grey-700"
                          >
                            {t(labelKey)}
                            {isMissing && (
                              <span className="ml-1 rounded-full bg-warning-bg px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                                !
                              </span>
                            )}
                          </label>
                          {field === "color" ? (
                            <ColorSelect
                              id={`edit-${field}`}
                              value={form.color ?? ""}
                              options={colorOptions}
                              isLoading={colorsLoading}
                              isMissing={isMissing}
                              placeholder={t("import.agreements.edit.colorPlaceholder")}
                              onChange={(name) => {
                                setForm((prev) => ({ ...prev, color: name }));
                                setSaveError(null);
                              }}
                            />
                          ) : isTextarea ? (
                            <Textarea
                              id={`edit-${field}`}
                              value={form[field] ?? ""}
                              onChange={handleChange(field)}
                              rows={3}
                              className={`resize-none${isMissing ? " border-warning-border" : ""}`}
                            />
                          ) : type === "date" ? (
                            <DatePicker
                              id={`edit-${field}`}
                              value={form[field] ?? ""}
                              onChange={(v) => {
                                setForm((prev) => ({ ...prev, [field]: v }));
                                setSaveError(null);
                              }}
                              className={isMissing ? "border-warning-border" : undefined}
                            />
                          ) : (
                            <Input
                              id={`edit-${field}`}
                              type={type}
                              value={form[field] ?? ""}
                              onChange={handleChange(field)}
                              className={isMissing ? "border-warning-border" : undefined}
                            />
                          )}
                        </div>
                      );
                    })}
                </div>

                {saveError && (
                  <div className="mt-4 flex items-center gap-2 rounded-card bg-red-bg px-4 py-3 text-[13px] text-red">
                    <AlertCircle size={16} />
                    {saveError}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {detail && !isLoading && (
          <div className="flex justify-end gap-3 border-t border-grey-100 px-4 py-4 sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-control border border-grey-200 px-5 py-2.5 text-[14px] font-medium text-grey-700 hover:bg-grey-50"
            >
              {t("import.agreements.edit.cancelBtn")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-control bg-green px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95 disabled:opacity-60 disabled:active:scale-100"
            >
              {isSaving && <Loader2 size={16} strokeWidth={2} className="animate-spin" />}
              {isSaving ? t("app.loading") : t("import.agreements.edit.saveBtn")}
            </button>
          </div>
        )}

        {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      </div>
    </div>,
    document.body,
  );
}
